import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent,
  Menu, MenuItem, IconButton, Paper, Slider,
  FormControl, FormLabel, CircularProgress, Alert, Tabs, Tab
} from '@mui/material';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Scatter, Line } from 'react-chartjs-2';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import * as XLSX from 'xlsx';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CloseIcon from '@mui/icons-material/Close';

const polarGridPlugin = {
  id: 'polarGrid',
  beforeDraw: (chart) => {
    const { ctx, scales } = chart;
    const centerX = scales.x.getPixelForValue(0);
    const centerY = scales.y.getPixelForValue(0);
    const maxRadius = 255;
    const step = 51;
    const fontSize = 12;
    const pxPerUnit = Math.abs(scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0));

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 1;
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let r = step; r <= maxRadius; r += step) {
      const radiusPx = pxPerUnit * r;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radiusPx, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fillText(`${r}`, centerX + radiusPx + 15, centerY);
    }

    for (let angle = 0; angle < 360; angle += 30) {
      const radians = -angle * (Math.PI / 180);
      const x = centerX + Math.cos(radians) * pxPerUnit * maxRadius;
      const y = centerY + Math.sin(radians) * pxPerUnit * maxRadius;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();

      const labelDist = pxPerUnit * (maxRadius + 25);
      const lx = centerX + Math.cos(radians) * labelDist;
      const ly = centerY + Math.sin(radians) * labelDist;
      ctx.fillText(`${angle}°`, lx, ly);
    }

    ctx.font = `bold 14px sans-serif`;
    ctx.fillText('Hue →', centerX + pxPerUnit * (maxRadius + 60), centerY + 20);

    ctx.save();
    ctx.translate(centerX - 20, centerY - pxPerUnit * (maxRadius + 60));
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Saturation ↑', 0, 0);
    ctx.restore();

    ctx.restore();
  }
};

// Register Chart.js components (but NOT the polarGridPlugin globally)
ChartJS.register(LinearScale, PointElement, LineElement, CategoryScale, Filler, Tooltip, Legend, zoomPlugin);

// Consistent color palette for objects
const OBJECT_COLORS = [
  { r: 255, g: 99, b: 132 },
  { r: 54, g: 162, b: 235 },
  { r: 255, g: 206, b: 86 },
  { r: 75, g: 192, b: 192 },
  { r: 153, g: 102, b: 255 },
  { r: 255, g: 159, b: 64 },
  { r: 199, g: 199, b: 199 },
  { r: 83, g: 102, b: 255 },
  { r: 255, g: 99, b: 255 },
  { r: 99, g: 255, b: 132 },
];

const getObjectColor = (index) => OBJECT_COLORS[index % OBJECT_COLORS.length];



// Utility
const polarToCartesian = (hue, saturation) => {
  const angle = (hue * Math.PI) / 180; // counterclockwise, 0° = right
  return { x: Math.cos(angle) * saturation, y: Math.sin(angle) * saturation };
};

const PigmentationAnalysisChart = ({ isOpen, onClose, onApiCall, objectMeta }) => {
  const [data, setData] = useState(null);
  const [currentFrameIdx, setCurrentFrameIdx] = useState(0);
  const [maxFrameIdx, setMaxFrameIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(500);
  const [activeTab, setActiveTab] = useState(0); // 0=Polar, 1=Hue, 2=Saturation, 3=Value
  const intervalRef = useRef(null);
  const canvasRef = useRef(null);
  const hueChartRef = useRef(null);
  const satChartRef = useRef(null);
  const valChartRef = useRef(null);

  useEffect(() => {
    if (isOpen) fetchData();
    return () => clearInterval(intervalRef.current);
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await onApiCall();
      if (!result.data_url) throw new Error('Missing data URL');
      const response = await fetch(result.data_url);
      if (!response.ok) throw new Error('Failed to load data');
      const pigmentationData = await response.json();
      setData(pigmentationData);
      setCurrentFrameIdx(0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Build sorted numeric list of available frame keys
  const availableFrames = useMemo(() => {
    const framesNode = data?.frames || {};
    return Object.keys(framesNode)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
  }, [data]);

  // Collect all unique object IDs across all frames
  const allObjectIds = useMemo(() => {
    const ids = new Set();
    const framesNode = data?.frames || {};
    Object.values(framesNode).forEach(frame => {
      Object.keys(frame).forEach(id => ids.add(id));
    });
    return Array.from(ids).sort((a, b) => Number(a) - Number(b));
  }, [data]);

  // Keep max index in sync with available frames
  useEffect(() => {
    setMaxFrameIdx(Math.max(0, availableFrames.length - 1));
    setCurrentFrameIdx((idx) =>
      Math.min(Math.max(0, idx), Math.max(0, availableFrames.length - 1))
    );
  }, [availableFrames]);

  const hsvToRgb = (h, s, v) => {
    s /= 255; v /= 255;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else if (h < 360) [r, g, b] = [c, 0, x];

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    };
  };

  // ====== POLAR HSV CHART ======
  const createPolarChartData = (frameData) => {
    if (!frameData) return { datasets: [] };
    return {
      datasets: Object.entries(frameData).map(([objectId, obj]) => {
        const hue = obj.hue || 0;
        const saturation = obj.saturation || 0;
        const value = obj.value || 0;
        const brightness = value / 255;
        const alpha = Math.max(0.3, brightness);

        const { r, g, b } = hsvToRgb(hue, saturation, value);
        const { x, y } = polarToCartesian(hue, saturation);

        return {
          label: objectMeta?.[objectId]?.name || `Object ${objectId}`,
          data: [{ x, y, ...obj }],
          backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})`,
          borderColor: `rgba(${r}, ${g}, ${b}, 1)`,
          borderWidth: 2,
          pointRadius: 6 + brightness * 10,
          pointHoverRadius: 8 + brightness * 15
        };
      })
    };
  };

  const polarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    scales: {
      x: { type: 'linear', min: -260, max: 260, grid: { display: false }, ticks: { display: false }, title: { display: false }, border: { display: false } },
      y: { type: 'linear', min: -260, max: 260, grid: { display: false }, ticks: { display: false }, title: { display: false }, border: { display: false } }
    },
    plugins: {
      legend: {
        position: 'right',
        labels: { color: '#fff', padding: 20, boxWidth: 20, boxHeight: 10, font: { size: 14 } }
      },
      title: { display: true, text: 'HSV Polar Plot', color: '#fff', font: { size: 18 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const d = ctx.raw;
            return [
              `Hue: ${d.hue?.toFixed(1)}°`,
              `Saturation: ${d.saturation?.toFixed(1)}`,
              `Value: ${d.value?.toFixed(1)}`
            ];
          }
        }
      },
      zoom: {
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' },
        pan: { enabled: true, mode: 'xy' }
      }
    }
  };

  // ====== DISTRIBUTION CHARTS ======
  const buildDistributionData = useCallback((property, stdProperty) => {
    if (!data?.frames || availableFrames.length === 0) return { labels: [], datasets: [] };

    const labels = availableFrames.map((fIdx) => {
      const frameData = data.frames[String(fIdx)];
      const firstObj = frameData ? Object.values(frameData)[0] : null;
      const ts = firstObj?.timestamp;
      if (ts) {
        const d = new Date(ts);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const hh = String(d.getHours()).padStart(2, '0');
          const min = String(d.getMinutes()).padStart(2, '0');
          return `${mm}-${dd} ${hh}:${min}`;
        }
      }
      return firstObj?.frame_name || `Frame ${fIdx}`;
    });

    const datasets = [];
    allObjectIds.forEach((objId, colorIdx) => {
      const color = getObjectColor(colorIdx);
      const objName = objectMeta?.[objId]?.name || `Object ${objId}`;

      const meanValues = availableFrames.map(fIdx => {
        const val = data.frames[String(fIdx)]?.[objId]?.[property];
        return val != null ? parseFloat(val.toFixed(2)) : null;
      });

      // Mean line
      datasets.push({
        label: objName,
        data: meanValues,
        borderColor: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
        backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, 0.1)`,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
        tension: 0.3,
        spanGaps: true,
        fill: false
      });

      // ±1 Std deviation band
      if (stdProperty) {
        const upperBound = availableFrames.map(fIdx => {
          const mean = data.frames[String(fIdx)]?.[objId]?.[property];
          const std = data.frames[String(fIdx)]?.[objId]?.[stdProperty];
          return (mean != null && std != null) ? parseFloat((mean + std).toFixed(2)) : null;
        });
        const lowerBound = availableFrames.map(fIdx => {
          const mean = data.frames[String(fIdx)]?.[objId]?.[property];
          const std = data.frames[String(fIdx)]?.[objId]?.[stdProperty];
          return (mean != null && std != null) ? parseFloat(Math.max(0, mean - std).toFixed(2)) : null;
        });

        datasets.push({
          label: `${objName} ±1σ`,
          data: upperBound,
          borderColor: 'transparent',
          backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, 0.15)`,
          borderWidth: 0,
          pointRadius: 0,
          fill: '+1',
          tension: 0.3,
          spanGaps: true
        });

        datasets.push({
          label: `_hidden_lower_${objId}`,
          data: lowerBound,
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          borderWidth: 0,
          pointRadius: 0,
          fill: false,
          tension: 0.3,
          spanGaps: true
        });
      }
    });

    return { labels, datasets };
  }, [data, availableFrames, allObjectIds, objectMeta]);

  const hueDistData = useMemo(() => buildDistributionData('hue', 'hue_std'), [buildDistributionData]);
  const satDistData = useMemo(() => buildDistributionData('saturation', 'saturation_std'), [buildDistributionData]);
  const valDistData = useMemo(() => buildDistributionData('value', 'value_std'), [buildDistributionData]);

  const makeDistChartOptions = (title, yLabel, yMax) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        ticks: { color: '#aaa', maxRotation: 45, autoSkip: true, maxTicksLimit: 20, font: { size: 10 } },
        grid: { color: 'rgba(255,255,255,0.08)' },
        title: { display: true, text: 'Date & Time (MM-DD HH:MM)', color: '#ccc', font: { size: 12 } }
      },
      y: {
        min: 0,
        max: yMax,
        ticks: { color: '#aaa', font: { size: 11 } },
        grid: { color: 'rgba(255,255,255,0.08)' },
        title: { display: true, text: yLabel, color: '#ccc', font: { size: 13 } }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          padding: 15,
          font: { size: 12 },
          filter: (item) => !item.text.startsWith('_hidden_')
        }
      },
      title: { display: true, text: title, color: '#fff', font: { size: 16 } },
      tooltip: {
        filter: (item) => !item.dataset.label?.startsWith('_hidden_'),
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`
        }
      },
      zoom: {
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' },
        pan: { enabled: true, mode: 'x' }
      }
    }
  });

  const hueChartOptions = useMemo(() => makeDistChartOptions('Hue Distribution Across Frames', 'Hue (°)', 360), []);
  const satChartOptions = useMemo(() => makeDistChartOptions('Saturation Distribution Across Frames', 'Saturation (0–255)', 260), []);
  const valChartOptions = useMemo(() => makeDistChartOptions('Brightness (Value) Distribution Across Frames', 'Value (0–255)', 260), []);

  const playAnimation = () => {
    if (isPlaying) {
      clearInterval(intervalRef.current);
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentFrameIdx((prev) => {
        if (prev >= maxFrameIdx) {
          clearInterval(intervalRef.current);
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, playbackSpeed);
  };

  const stopAnimation = () => {
    clearInterval(intervalRef.current);
    setIsPlaying(false);
    setCurrentFrameIdx(0);
  };

  // ====== DOWNLOADS ======
  const resetZoom = useCallback(() => {
    const refs = [canvasRef, hueChartRef, satChartRef, valChartRef];
    const ref = refs[activeTab];
    const chart = ref?.current;
    if (chart && chart.resetZoom) chart.resetZoom();
  }, [activeTab]);

  const downloadXlsx = () => {
    if (!data?.frames) return;
    const rows = [['Frame_Index', 'Frame_Name', 'Timestamp', 'Object_ID', 'Object_Name',
      'Hue_Mean', 'Hue_Std', 'Saturation_Mean', 'Saturation_Std',
      'Value_Mean', 'Value_Std', 'Pixel_Count']];

    availableFrames.forEach(fIdx => {
      const frameData = data.frames[String(fIdx)];
      if (!frameData) return;
      Object.entries(frameData).forEach(([objId, d]) => {
        rows.push([
          fIdx,
          d.frame_name || '',
          d.timestamp || '',
          objId,
          objectMeta?.[objId]?.name || `Object ${objId}`,
          d.hue?.toFixed(2) ?? '',
          d.hue_std?.toFixed(2) ?? '',
          d.saturation?.toFixed(2) ?? '',
          d.saturation_std?.toFixed(2) ?? '',
          d.value?.toFixed(2) ?? '',
          d.value_std?.toFixed(2) ?? '',
          d.pixel_count ?? ''
        ]);
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Pigmentation Data');
    XLSX.writeFile(wb, 'pigmentation_analysis_data.xlsx');
  };

  const downloadCurrentChart = () => {
    let chartCanvas = null;
    if (activeTab === 0) chartCanvas = canvasRef.current?.canvas;
    else if (activeTab === 1) chartCanvas = hueChartRef.current?.canvas;
    else if (activeTab === 2) chartCanvas = satChartRef.current?.canvas;
    else if (activeTab === 3) chartCanvas = valChartRef.current?.canvas;

    if (!chartCanvas) return;
    const link = document.createElement('a');
    link.href = chartCanvas.toDataURL('image/png');
    const tabNames = ['hsv_polar', 'hue_distribution', 'saturation_distribution', 'value_distribution'];
    const realFrameNumber = availableFrames[currentFrameIdx] ?? 0;
    link.download = activeTab === 0
      ? `${tabNames[0]}_frame_${realFrameNumber}.png`
      : `${tabNames[activeTab]}.png`;
    link.click();
  };

  if (!isOpen) return null;

  const realFrameNumber = availableFrames[currentFrameIdx] ?? 0;
  const currentFrameData = data?.frames?.[String(realFrameNumber)] || {};
  const polarChartData = createPolarChartData(currentFrameData);
  const hasData = availableFrames.length > 0;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{ style: { height: '90vh', backgroundColor: '#000' } }}
    >
      <DialogTitle sx={{ backgroundColor: '#000', color: '#fff', pb: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h6">Color Analysis</Typography>
          <Box>
            <IconButton onClick={resetZoom} title="Reset Zoom">
              <RestartAltIcon sx={{ color: '#fff' }} />
            </IconButton>
            <IconButton onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}>
              <DownloadIcon sx={{ color: '#fff' }} />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Box>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            mt: 1,
            '& .MuiTab-root': { color: '#888', minHeight: 40, textTransform: 'none', fontSize: 13 },
            '& .Mui-selected': { color: '#fff' }
          }}
        >
          <Tab label="HSV Polar" />
          <Tab label="Hue" />
          <Tab label="Saturation" />
          <Tab label="Brightness" />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#000', color: '#ccc', pt: 2 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" height={200}>
            <CircularProgress />
            <Typography sx={{ ml: 2, color: '#ccc' }}>Loading data...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !hasData ? (
          <Alert severity="info">No pigmentation frames available.</Alert>
        ) : (
          <Box>
            {/* ===== TAB 0: POLAR HSV ===== */}
            {activeTab === 0 && (
              <>
                <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#111' }}>
                  <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mb: 1 }}>
                    <strong>How to read:</strong> Hue = angle (0° = red, counterclockwise), Saturation = distance from center (0–255), Value = dot size &amp; brightness
                  </Typography>
                  <Box sx={{ width: '100%', maxHeight: '45vh', aspectRatio: '1', margin: '0 auto', backgroundColor: '#000', borderRadius: 1 }}>
                    <Scatter ref={canvasRef} data={polarChartData} options={polarChartOptions} plugins={[polarGridPlugin]} />
                  </Box>
                </Paper>

                {/* Playback controls */}
                <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#111', color: '#ccc' }}>
                  <Typography variant="subtitle1" gutterBottom>Playback Controls</Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton onClick={() => setCurrentFrameIdx(f => Math.max(0, f - 1))} disabled={currentFrameIdx === 0}>
                        <SkipPreviousIcon sx={{ color: '#fff' }} />
                      </IconButton>
                      <IconButton onClick={playAnimation}>
                        {isPlaying ? <PauseIcon sx={{ color: '#fff' }} /> : <PlayArrowIcon sx={{ color: '#fff' }} />}
                      </IconButton>
                      <IconButton onClick={stopAnimation}>
                        <StopIcon sx={{ color: '#fff' }} />
                      </IconButton>
                      <IconButton onClick={() => setCurrentFrameIdx(f => Math.min(maxFrameIdx, f + 1))} disabled={currentFrameIdx === maxFrameIdx}>
                        <SkipNextIcon sx={{ color: '#fff' }} />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Frame {realFrameNumber} ({currentFrameIdx + 1} / {maxFrameIdx + 1})
                    </Typography>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel sx={{ color: '#ccc' }}>Frame</FormLabel>
                    <Slider
                      value={currentFrameIdx}
                      min={0}
                      max={maxFrameIdx}
                      step={1}
                      onChange={(_, value) => setCurrentFrameIdx(value)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(idx) => availableFrames[idx] ?? idx}
                      disabled={isPlaying}
                      sx={{ color: '#888' }}
                    />
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <FormLabel sx={{ color: '#fff' }}>Playback Speed</FormLabel>
                    <Slider
                      value={1000 - playbackSpeed + 100}
                      min={100}
                      max={1000}
                      step={50}
                      onChange={(_, value) => setPlaybackSpeed(1000 - value + 100)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${((1000 - value + 100) / 1000).toFixed(1)}x`}
                    />
                  </FormControl>
                </Paper>
              </>
            )}

            {/* ===== TAB 1: HUE DISTRIBUTION ===== */}
            {activeTab === 1 && (
              <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#111' }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mb: 1 }}>
                  Mean Hue (°) per object across measured frames. Shaded region = ±1 standard deviation.
                </Typography>
                <Box sx={{ height: 450, backgroundColor: '#000', borderRadius: 1, p: 1 }}>
                  <Line ref={hueChartRef} data={hueDistData} options={hueChartOptions} />
                </Box>
              </Paper>
            )}

            {/* ===== TAB 2: SATURATION DISTRIBUTION ===== */}
            {activeTab === 2 && (
              <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#111' }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mb: 1 }}>
                  Mean Saturation (0–255) per object across measured frames. Shaded region = ±1 standard deviation.
                </Typography>
                <Box sx={{ height: 450, backgroundColor: '#000', borderRadius: 1, p: 1 }}>
                  <Line ref={satChartRef} data={satDistData} options={satChartOptions} />
                </Box>
              </Paper>
            )}

            {/* ===== TAB 3: VALUE (BRIGHTNESS) DISTRIBUTION ===== */}
            {activeTab === 3 && (
              <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#111' }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mb: 1 }}>
                  Mean Brightness / Value (0–255) per object across measured frames. Shaded region = ±1 standard deviation.
                </Typography>
                <Box sx={{ height: 450, backgroundColor: '#000', borderRadius: 1, p: 1 }}>
                  <Line ref={valChartRef} data={valDistData} options={valChartOptions} />
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
        PaperProps={{ sx: { backgroundColor: '#111', color: '#fff' } }}
      >
        <MenuItem onClick={() => { downloadXlsx(); setDownloadMenuAnchor(null); }}>
          Download Raw Data (XLSX)
        </MenuItem>
        <MenuItem onClick={() => { downloadCurrentChart(); setDownloadMenuAnchor(null); }}>
          Download Current Chart (PNG)
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default PigmentationAnalysisChart;